import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  startTransition,
} from "react";
import { styled } from "@mui/material/styles";
import MuiDrawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import {
  logout,
  getUserDetails,
  isAdminUser,
  getSessionId,
} from "../../services/authService";
import { getCustomerLinks } from "../../services/fhpService";
import { useAgentEntitlements } from "../../features/agentEntitlements";
import { useDevViewModeOptional } from "../../dev/viewMode";
import { navigateAgentPackageUpgrade } from "../../utils/agentUpgradeNavigation";
import { CRM_HUB_ADMIN_ONLY } from "../../utils/crmHubFlag";
import { useCrmHubEnabled } from "../../utils/useCrmHubEnabled";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HOTELS,
  PACKAGES,
  DYNAMIC_PACKAGES,
  useSearch,
} from "../../contexts/SearchContext";
import {
  GlobalOutlined,
  PlayCircleOutlined,
  LockOutlined,
  FileTextOutlined,
  BellOutlined,
  ReadOutlined,
  LayoutOutlined,
  RobotOutlined,
  CustomerServiceOutlined,
  CommentOutlined,
  FormOutlined,
  IdcardOutlined,
  HomeOutlined,
  SearchOutlined,
  BookOutlined,
  ProfileOutlined,
  BarChartOutlined,
  SettingOutlined,
  TeamOutlined,
  PieChartOutlined,
  BankOutlined,
} from "@ant-design/icons";
import { Badge } from "antd";

import "./SideNav.css";
import {
  getSideBarStatus,
  // getUserInfo,
  setSideBarStatus,
} from "../../services/localStorage";
import ProfileImageUploader from "../ProfileImageUploader/ProfileImageUploader";
import { useAppContext } from "../../contexts/AppContext";
// import { Title } from "../Text";

const drawerWidth = 250;

const getDraftCount = () => {
  try {
    const raw = localStorage.getItem("agent_drafts");
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.length;
    if (parsed && typeof parsed === "object") {
      if (Array.isArray(parsed.drafts)) return parsed.drafts.length;
      if (Array.isArray(parsed.items)) return parsed.items.length;
      if (typeof parsed.length === "number") return parsed.length;
    }
    return 0;
  } catch (error) {
    return 0;
  }
};

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div", {
  shouldForwardProp: (prop) => prop !== "isRTL",
})(({ isRTL }) => ({
  minHeight: 0,
  height: "auto",
  display: "flex",
  alignItems: "center",
  justifyContent: isRTL ? "flex-end" : "flex-start",
  padding: 0,
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const SideNav = ({ setSidebarOpen }) => {
  // const theme = useTheme();
  const { t } = useTranslation();
  const { setAdminFocusMode } = useAppContext();
  const user = getUserDetails();
  const navigate = useNavigate();
  const location = useLocation();
  const { switchFromOrganizedToursIfNeeded, loading: searchResultsLoading } =
    useSearch();
  // const { imageprofilebase65 } = getUserInfo() || {};
  const devView = useDevViewModeOptional();
  const isAdmin =
    isAdminUser() &&
    !(devView?.isPackageOverrideActive) &&
    (!devView?.canSwitch || Boolean(devView?.presentAsAdmin));
  const crmHubEnabled = useCrmHubEnabled();

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    console.log("[ADMIN_DETECTION_DEBUG][SideNav]", {
      user,
      role: user?.role ?? user?.agentRole ?? null,
      usertype: user?.usertype ?? user?.userType ?? null,
      is_admin: user?.is_admin ?? user?.isAdmin ?? null,
      email: user?.email ?? null,
      isAdminUserResult: isAdmin,
    });
  }, [isAdmin, user]);

  useEffect(() => {
    if (!import.meta.env.DEV || !isAdmin) return;
    console.log("[ADMIN_ACADEMY_NAV_VISIBLE]", {
      path: "/p/dashboard/admin/academy",
    });
  }, [isAdmin]);

  const [open, setOpen] = React.useState(getSideBarStatus());
  const [draftCount, setDraftCount] = useState(0);
  const [offersCount, setOffersCount] = useState(0);
  const currentLanguage = i18n.language || "he";
  const isRTL = currentLanguage === "he";
  const drawerAnchor = isRTL ? "right" : "left";
  const pendingNavPerfRef = useRef(null);
  const paperRef = useRef(null);
  const listRef = useRef(null);
  if (
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    window.__RENDER_TRACE__ === true
  ) {
    console.count("[RENDER_TRACE] Sidenav");
  }

  // Update draft count
  useEffect(() => {
    const updateDraftCount = () => {
      const count = getDraftCount();
      setDraftCount((prev) => (prev === count ? prev : count));
    };

    // Initial load
    updateDraftCount();

    // Update on storage changes (when drafts are saved/deleted)
    const handleStorageChange = (e) => {
      if (e.key === 'agent_drafts' || !e.key) {
        updateDraftCount();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen to custom events for same-tab updates
    const handleDraftUpdate = () => {
      updateDraftCount();
    };
    
    window.addEventListener('draftUpdated', handleDraftUpdate);
    
    // Poll for changes every 2 seconds (fallback)
    const interval = setInterval(updateDraftCount, 2000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('draftUpdated', handleDraftUpdate);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchOffersCount = async () => {
      try {
        if (
          location.pathname.includes("searchoffer") &&
          searchResultsLoading
        ) {
          return;
        }
        const sessionID = await getSessionId();
        if (!sessionID) return;
        const response = await getCustomerLinks({
          sessionID,
          agent_id: isAdmin ? null : user?.id,
          limit: 1,
          offset: 0,
        });
        if (cancelled) return;
        const total = response?.data?.total_count;
        const n = typeof total === "number" ? total : parseInt(total, 10);
        if (!Number.isNaN(n) && n >= 0) {
          setOffersCount((prev) => (prev === n ? prev : n));
        }
      } catch {
        if (!cancelled) {
          setOffersCount((prev) => (prev === 0 ? prev : 0));
        }
      }
    };
    fetchOffersCount();
    const interval = setInterval(fetchOffersCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAdmin, user?.id, location.pathname, searchResultsLoading]);

  const isSearch = location.pathname.includes("searchoffer");

  const {
    status: entitlementsStatus,
    hasPrioritySupport,
    canUseAgentPublicSite,
    canUseManoQuotes,
  } = useAgentEntitlements();
  const siteSettingsNavLocked = !canUseAgentPublicSite;
  const manoQuotesNavLocked = !canUseManoQuotes;
  const showPriorityBadge =
    !isAdmin &&
    entitlementsStatus === "ok" &&
    hasPrioritySupport;

  // Check if a menu item is active based on current route
  const isActive = useCallback((path) => {
    if (typeof path === "string" && path.startsWith("/")) {
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    }
    const basePath = String(path || "").split("?")[0];
    if (basePath === "home") {
      return location.pathname === "/p/dashboard/home" || location.pathname === "/p/dashboard/";
    }
    return (
      location.pathname.includes(`/p/dashboard/${basePath}`) ||
      (basePath === "searchoffer" && location.pathname.includes("searchoffer"))
    );
  }, [location.pathname]);

  const adminMenuItems = useMemo(
    () =>
      isSearch
        ? [
            { text: t("sidenav.admin"), icon: PieChartOutlined, path: "admin" },
            {
              text: "סרטוני אקדמיה",
              icon: PlayCircleOutlined,
              path: "/p/dashboard/admin/academy",
            },
            {
              text: t("priceoffer.editclientsinfo"),
              icon: TeamOutlined,
              path: "searchoffer?clearuser=true",
            },
          ]
        : [
            { text: t("sidenav.admin"), icon: PieChartOutlined, path: "admin" },
            {
              text: "סרטוני אקדמיה",
              icon: PlayCircleOutlined,
              path: "/p/dashboard/admin/academy",
            },
          ],
    [isSearch, t]
  );

  const menuItems = useMemo(
    () => [
      { text: t("sidenav.home"), icon: HomeOutlined, path: "home" },
      {
        text: t("sidenav.searchoffer"),
        icon: SearchOutlined,
        path: "searchoffer",
        isSearchNav: true,
      },
      {
        text: t("sidenav.academy"),
        icon: BookOutlined,
        path: "academy",
      },
      {
        text: "Community",
        icon: CommentOutlined,
        path: "community",
      },
      { text: t("sidenav.orders"), icon: ProfileOutlined, path: "orders" },
      // Operation-request compose / "הפניות שלי" removed from sidebar —
      // agents open that flow from CRM docket tabs; routes stay registered for deep-links.
      {
        text: "הודעות מערכת",
        icon: BellOutlined,
        path: "reminders",
      },
      {
        text: crmHubEnabled ? "CRM — לידים, הצעות ודוקטים" : "טיוטות והצעות בהליך",
        icon: FileTextOutlined,
        path: "drafts",
      },
      {
        text: "הצעות מנו",
        icon: BankOutlined,
        path: "mano-quotes",
      },
      {
        text: "דרכונים — מנו",
        icon: IdcardOutlined,
        path: "mano-quotes/passport-uploads",
      },
      { text: t("sidenav.finance"), icon: BarChartOutlined, path: "finance" },
      { text: "SEO", icon: GlobalOutlined, path: "seo" },
      { text: "SEO מאמרים", icon: ReadOutlined, path: "seo/articles" },
      { text: "הגדרות האתר שלי", icon: LayoutOutlined, path: "site-settings" },
      { text: t("sidenav.management"), icon: SettingOutlined, path: "management" },
      ...(!CRM_HUB_ADMIN_ONLY || isAdmin
        ? [
            {
              text: "לידים AI",
              icon: RobotOutlined,
              path: crmHubEnabled ? "drafts?tab=leads" : "ai-sales-leads",
            },
          ]
        : []),
      {
        text: "צ'אט לקוחות",
        icon: CustomerServiceOutlined,
        path: "customer-chat-inbox",
      },
      ...(isAdmin
        ? [
            {
              text: "פניות אופרציה",
              icon: FormOutlined,
              path: "admin/operation-requests",
            },
            ...adminMenuItems,
          ]
        : []),
    ],
    [adminMenuItems, crmHubEnabled, isAdmin, t]
  );

  useEffect(() => {
    const paper = paperRef.current;
    const list = listRef.current;
    if (!paper) return;

    const applyDensity = () => {
      let safe = 0;
      const overlay = document.querySelector(".hr-dev-view-mode");
      if (overlay) {
        const paperRect = paper.getBoundingClientRect();
        const overlayRect = overlay.getBoundingClientRect();
        const overlapW =
          Math.min(paperRect.right, overlayRect.right) -
          Math.max(paperRect.left, overlayRect.left);
        const overlapH =
          Math.min(paperRect.bottom, overlayRect.bottom) -
          Math.max(paperRect.top, overlayRect.top);
        if (overlapW > 8 && overlapH > 8) {
          safe = Math.ceil(overlapH + 8);
        }
      }
      paper.style.setProperty("--sn-dev-safe", `${safe}px`);

      const paperH = paper.clientHeight;
      paper.dataset.sidenavDensity =
        paperH < 700 ? "dense" : paperH < 860 ? "compact" : "comfortable";
    };

    applyDensity();
    const ro = new ResizeObserver(applyDensity);
    ro.observe(paper);
    if (list) ro.observe(list);
    const overlay = document.querySelector(".hr-dev-view-mode");
    if (overlay) ro.observe(overlay);
    window.addEventListener("resize", applyDensity);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", applyDensity);
    };
  }, [isRTL, open, menuItems.length]);

  const logoutAgent = useCallback(() => {
    logout();
    window.location.href = "/login";
  }, []);

  const toggleSidebar = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      setSidebarOpen((prevExternal) => {
        if (prevExternal === next) return prevExternal;
        return next;
      });
      setSideBarStatus(next);
      return next;
    });
  }, [setSidebarOpen]);

  const handleLinkClick = useCallback((path, isExternal = false) => {
    if (isExternal) {
      startTransition(() => {
        navigate(`/${path}`);
      });
      return;
    }
    if (typeof path === "string" && path.startsWith("/")) {
      startTransition(() => {
        navigate(path);
      });
      return;
    }
    const base = String(path).split("?")[0];
    let searchProductTab = null;
    if (base === "searchhotels") searchProductTab = HOTELS;
    else if (base === "searchdynamic") searchProductTab = DYNAMIC_PACKAGES;
    else if (base === "searchpackages" || base === "searchoffer") searchProductTab = PACKAGES;
    if (searchProductTab) {
      switchFromOrganizedToursIfNeeded(searchProductTab);
    }
    if (base === "admin" || base.startsWith("admin/")) {
      setAdminFocusMode(true);
    }
    startTransition(() => {
      navigate(`/p/dashboard/${path}`);
    });
  }, [navigate, setAdminFocusMode, location.pathname, switchFromOrganizedToursIfNeeded]);

  const handleMenuItemClick = useCallback(
    (item) => {
      if (item?.disabled) return;
      if (item?.path === "site-settings" && siteSettingsNavLocked) {
        navigateAgentPackageUpgrade(navigate, "side_nav_site_settings_locked");
        return;
      }
      const manoPath =
        item?.path === "mano-quotes" ||
        item?.path === "mano-quotes/passport-uploads";
      if (manoPath && manoQuotesNavLocked) {
        navigateAgentPackageUpgrade(navigate, "side_nav_mano_quotes_locked");
        return;
      }
      const clickTs = performance.now();
      pendingNavPerfRef.current = {
        clickTs,
        path: item?.path || null,
      };
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!pendingNavPerfRef.current) return;
          const frameLatencyMs = Math.round((performance.now() - clickTs) * 100) / 100;
          if (import.meta.env.DEV) {
            }
        });
      });
      if (import.meta.env.DEV) {
        }
      handleLinkClick(item.path, item.isExternal);
    },
    [
      handleLinkClick,
      location.pathname,
      navigate,
      open,
      siteSettingsNavLocked,
      manoQuotesNavLocked,
    ]
  );

  useEffect(() => {
    if (!pendingNavPerfRef.current) return;
    const { clickTs, path } = pendingNavPerfRef.current;
    const commitLatencyMs = Math.round((performance.now() - clickTs) * 100) / 100;
    if (import.meta.env.DEV) {
      }
    pendingNavPerfRef.current = null;
  }, [location.pathname]);

  const renderUserType = () => {
    return isAdmin ? (
      <div className="side-nav-admin-badge">אדמין</div>
    ) : null;
  };

  const adminBadgeNode = renderUserType();

  return (
    <div className={"SideNav-wrapper"}>
      <Drawer
        anchor={drawerAnchor}
        variant="permanent"
        open={open}
        PaperProps={{
          ref: paperRef,
          className: "side-nav-paper",
          sx: {
            width: open ? 240 : 72,
            height: "100dvh",
            backgroundColor: "#132a4a",
            backgroundImage: "linear-gradient(180deg, #0b1f3a 0%, #132a4a 100%)",
            borderRight: "none",
            borderLeft: "none",
            boxShadow: "none",
            color: "#ffffff",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DrawerHeader className="side-nav-login-user" isRTL={isRTL}>
          {user && open ? (
            <div
              className="side-nav-header-inner user-details"
              dir={isRTL ? "rtl" : "ltr"}
            >
              <div className="side-nav-header-top">
                <div className="side-nav-header-brand">
                  <ProfileImageUploader />
                </div>
                <div
                  onClick={toggleSidebar}
                  className="arrow-wrapper arrow-wrapper-open"
                >
                  {isRTL ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                </div>
              </div>
              <div
                className="side-nav-login-user-item side-nav-header-user"
                style={{ textAlign: isRTL ? "right" : "left" }}
              >
                {t("sidenav.greetings")},<br />
                {user.firstname} {user.lastname}
              </div>
              {(adminBadgeNode || showPriorityBadge) && (
                <div className="side-nav-header-badges">
                  {adminBadgeNode}
                  {showPriorityBadge && (
                    <div
                      className="dashboard-header-status-chips"
                      role="group"
                      aria-label="תמיכה"
                    >
                      <span
                        className="dashboard-status-chip dashboard-status-chip--plan"
                        style={{ cursor: "default", opacity: 0.95 }}
                        title="תמיכה מועדפת"
                      >
                        תמיכה מועדפת
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="side-nav-header-inner side-nav-header-inner--collapsed">
              <div
                onClick={toggleSidebar}
                className={`arrow-wrapper arrow-wrapper-${
                  open ? "open" : "closed"
                }`}
              >
                {isRTL ? (
                  open ? <ChevronRightIcon /> : <ChevronLeftIcon />
                ) : open ? (
                  <ChevronLeftIcon />
                ) : (
                  <ChevronRightIcon />
                )}
              </div>
            </div>
          )}
        </DrawerHeader>
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />
        <List
          ref={listRef}
          className="side-nav-menu-list"
          sx={{
            py: 0,
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          {menuItems.map(
            ({
              text,
              icon: IconComponent,
              path,
              disabled,
              isExternal,
              isSearchNav,
            }) => {
            const active = isActive(path);
            const siteSettingsLockedRow = siteSettingsNavLocked && path === "site-settings";
            const manoQuotesLockedRow =
              manoQuotesNavLocked &&
              (path === "mano-quotes" || path === "mano-quotes/passport-uploads");
            const entitlementLockedRow = siteSettingsLockedRow || manoQuotesLockedRow;
            return (
              <ListItem
                key={text}
                disablePadding
                sx={{
                  display: "block",
                  flex: isSearchNav ? "1.18 1 0" : "1 1 0",
                  minHeight: 0,
                  maxHeight: isSearchNav
                    ? "var(--sn-search-row-max)"
                    : "var(--sn-row-max)",
                  marginBottom: 0,
                }}
                onClick={() =>
                  handleMenuItemClick({
                    path,
                    isExternal,
                    disabled,
                  })
                }
              >
                <Tooltip
                  title={
                    disabled
                      ? t("notifications.soon")
                      : siteSettingsLockedRow
                        ? "זמין בחבילת PRO ומעלה"
                        : manoQuotesLockedRow
                          ? "זמין לאחר הפעלה על ידי אדמין בניהול חבילות"
                          : undefined
                  }
                  sx={{
                    fontSize: "6.2rem",
                    "& .MuiTooltip-tooltip": {
                      padding: "10px 15px",
                    },
                  }}
                >
                  <span>
                    <ListItemButton
                      disabled={disabled}
                      className={`side-nav-menu-item ${isSearchNav ? "search-nav-item" : ""} ${active ? "active-menu-item" : ""}`}
                      sx={{
                        minHeight: 0,
                        height: "100%",
                        justifyContent: open ? "initial" : "center",
                        px: open ? "var(--sn-item-px)" : 1,
                        mx: "var(--sn-item-mx)",
                        borderRadius: "var(--sn-radius)",
                        transition:
                          "background-color 0.2s ease-in-out, transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
                        position: "relative",
                        backgroundColor: active ? "#fe3702" : "transparent",
                        border: "1px solid transparent",
                        boxShadow: "none",
                        "& .MuiListItemText-primary": {
                          color: active ? "#ffffff" : "rgba(255, 255, 255, 0.82)",
                          fontWeight: active ? 700 : 500,
                        },
                        "& .side-nav-ant-icon": {
                          color: "#ffffff",
                        },
                        ...(!disabled && {
                          "&:hover": {
                            backgroundColor: active ? "#fe3702" : "rgba(255, 255, 255, 0.1)",
                          },
                        }),
                        ...(entitlementLockedRow && {
                          opacity: 0.58,
                          cursor: "not-allowed",
                        }),
                        ...(disabled && {
                          "& .MuiListItemText-primary": {
                            color: "#94a3b8",
                          },
                          "& .side-nav-ant-icon": {
                            opacity: 0.4,
                          },
                        }),
                      }}
                    >
                      <ListItemText
                        primary={
                          entitlementLockedRow ? (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <LockOutlined aria-hidden />
                              {text}
                            </span>
                          ) : (
                            text
                          )
                        }
                        sx={{
                          display: open ? "block" : "none",
                          opacity: open ? 1 : 0,
                          textAlign: isRTL ? "right" : "left",
                          transition: "opacity 0.2s ease",
                        }}
                      />
                      <ListItemIcon
                        className={path === "drafts" ? "side-nav-icon-with-badge" : ""}
                        sx={{
                          minWidth: path === "drafts" ? "var(--sn-search-plate)" : 0,
                          overflow: "visible",
                          ...(isRTL
                            ? { mr: open ? "var(--sn-item-px)" : "auto" }
                            : { ml: open ? "var(--sn-item-px)" : "auto" }
                          ),
                          justifyContent: "center",
                          transition:
                            "margin 0.2s ease, transform 0.2s ease, opacity 0.2s ease",
                          position: "relative",
                        }}
                      >
                        {path === "drafts" && (offersCount > 0 || draftCount > 0) ? (
                          <Badge
                            count={offersCount > 0 ? offersCount : draftCount}
                            overflowCount={999}
                            size="small"
                            offset={isRTL ? [-4, -8] : [4, -8]}
                            className="side-nav-offers-badge"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <IconComponent className="side-nav-ant-icon" />
                          </Badge>
                        ) : (
                          <IconComponent className="side-nav-ant-icon" />
                        )}
                      </ListItemIcon>
                    </ListItemButton>
                  </span>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>
        <Divider sx={{ borderColor: "rgba(255, 255, 255, 0.12)" }} />
        {open && (
          <ListItem
            onClick={logoutAgent}
            key={t("sidenav.logout")}
            disablePadding
            className="side-nav-logout-item"
            sx={{
              display: "block",
              marginTop: "auto",
              flexShrink: 0,
            }}
          >
            <ListItemButton
              className="side-nav-logout-button"
              sx={{
                minHeight: 0,
                justifyContent: open ? "initial" : "center",
                px: "var(--sn-item-px)",
                mx: "var(--sn-item-mx)",
                borderRadius: "var(--sn-radius)",
                backgroundColor: "transparent",
                transition:
                  "background-color 0.2s ease-in-out, transform 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
                "& .MuiListItemText-primary": {
                  color: "#ffffff",
                },
              }}
            >
              <ListItemText
                primary={t("sidenav.logout")}
                sx={{
                  opacity: open ? 1 : 0,
                  textAlign: isRTL ? "right" : "left",
                  "& .MuiListItemText-primary": {
                    color: "#ef4444",
                    fontWeight: 600,
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        )}
      </Drawer>
    </div>
  );
};

export default React.memo(SideNav);
